import { Injectable } from '@angular/core';
import { EMap, FilterPanelActions } from '@mode/reports/sandbox/contract';
import { EmbedReportSelectors, FlamingoSelectors } from './embed-report.selectors';
import { RangeValueFilterControl, LegacyReportFilterControl, ReportFilterControlJs } from '@mode/filters/util';
import { Observable, BehaviorSubject, distinctUntilChanged, map, combineLatest, filter } from 'rxjs';
import * as LegacyEmbedDatasetSelectors from './datasets/embed-datasets.selectors';
import { ModeModel, ReportTypes } from '@mode/shared/contract-common';
import { LegacyEmbedViewerStoreService } from './legacy-embed-viewer-store.service';
import { recordExtensionToJSObject } from '@mode/shared/util-js';

@Injectable({
  providedIn: 'root',
})
export class EmbedReportFilterFacade {
  filterIsExpanded$: Observable<boolean>;
  filterControls$: Observable<ReadonlyArray<ReportTypes.ReportFilterControl>>;
  filterOptions$: Observable<EMap<ReportTypes.ReportFilterOptions>>;
  filterOptionCounts$: Observable<EMap<number>>;
  filterOptionSelectedCounts$: Observable<EMap<number>>;
  datasetsLoading$: Observable<boolean>;
  orphanedFilters$: Observable<Set<string>>;
  isFilterPanelOpen$: Observable<boolean>;
  model$: Observable<ModeModel>;
  buttonPosition$: Observable<string>;

  private filterIsExpandedNotifier = new BehaviorSubject<boolean>(false);

  constructor(private store: LegacyEmbedViewerStoreService) {
    this.filterIsExpanded$ = this.filterIsExpandedNotifier.pipe(distinctUntilChanged());

    this.filterControls$ = this.store.select(EmbedReportSelectors.reportFilterControlRecords);
    this.filterOptions$ = this.store.select(EmbedReportSelectors.reportFilterOptions);
    this.filterOptionCounts$ = this.store.select(EmbedReportSelectors.reportFilterOptionsCount);
    this.filterOptionSelectedCounts$ = this.store.select(EmbedReportSelectors.reportFilterOptionsSelectedCount);
    this.isFilterPanelOpen$ = this.store.select(EmbedReportSelectors.isFilterPanelOpen);
    this.model$ = this.store.select(FlamingoSelectors.getModel);
    this.buttonPosition$ = combineLatest([
      this.store.selectNoEmpty(EmbedReportSelectors.viewerType),
      this.store.selectNoEmpty(EmbedReportSelectors.embedded),
    ]).pipe(
      map(([type, embedded]) => {
        if (type === 'whiteLabel' || embedded) {
          return 'top';
        }
        return 'bottom';
      })
    );

    this.datasetsLoading$ = this.store
      .select(LegacyEmbedDatasetSelectors.datasetResultsLoadedForAllRunDatasets)
      .pipe(map((loaded) => !loaded));

    this.orphanedFilters$ = this.store.select(EmbedReportSelectors.getOrphanedFilters).pipe(
      map((filters) => {
        return new Set(filters.map((filter) => filter.token));
      })
    );

    combineLatest([this.datasetsLoading$, this.filterControls$])
      .pipe(filter(([loading, filters]) => !loading && filters.length > 0))
      .subscribe(() => this.store.dispatch(new FilterPanelActions.FilterPanelLoaded()));
  }

  toggleFilterExpanded(isExpanded: boolean) {
    this.filterIsExpandedNotifier.next(isExpanded);
  }

  handleFilterPanelClose() {
    this.store.dispatch(new FilterPanelActions.ToggleFilterPanel(false));
  }

  handleFiltersApply(filters: LegacyReportFilterControl[]) {
    this.store.dispatch(new FilterPanelActions.ApplyFilters(filters.map((f) => recordExtensionToJSObject(f))));
  }

  handleFiltersSetDefault(filters: LegacyReportFilterControl[]) {
    this.store.dispatch(new FilterPanelActions.SetAsDefault(filters.map((f) => recordExtensionToJSObject(f))));
  }

  resetFilter(filter: LegacyReportFilterControl) {
    this.store.dispatch(new FilterPanelActions.ResetFilter(recordExtensionToJSObject(filter)));
  }

  resetAllFilters(filters: LegacyReportFilterControl[]) {
    this.store.dispatch(new FilterPanelActions.ResetFilters(filters.map((f) => recordExtensionToJSObject(f))));
  }

  deleteFilter(filter: LegacyReportFilterControl) {
    this.store.dispatch(new FilterPanelActions.DeleteFilter(recordExtensionToJSObject(filter)));
  }

  moveToBottom(filter: LegacyReportFilterControl) {
    this.store.dispatch(new FilterPanelActions.MoveFilterToBottom(recordExtensionToJSObject(filter)));
  }

  moveToPosition(filter: LegacyReportFilterControl, nextToken: string) {
    this.store.dispatch(new FilterPanelActions.MoveFilter(recordExtensionToJSObject(filter), nextToken));
  }

  selectionChanged(filter: LegacyReportFilterControl) {
    const control: ReportFilterControlJs = recordExtensionToJSObject(filter);
    this.store.dispatch(new FilterPanelActions.FilterSelectionChange(control.token, control.selected));
  }

  setFilterType(
    filter: LegacyReportFilterControl,
    filterType: ReportTypes.ReportFilterType,
    applyFilters: boolean,
    selection?: ReportTypes.FilterSelection
  ) {
    this.store.dispatch(
      new FilterPanelActions.SetFilterType(recordExtensionToJSObject(filter), filterType, applyFilters, selection)
    );
  }

  handleFilterSetSelectControlType(filter: LegacyReportFilterControl, controlType: ReportTypes.SelectControlType) {
    this.store.dispatch(new FilterPanelActions.SetSelectionControlType(recordExtensionToJSObject(filter), controlType));
  }

  handleFilterSetRangeIncludeNulls(filter: RangeValueFilterControl, includeNulls: boolean) {
    this.store.dispatch(new FilterPanelActions.SetRangeIncludeNulls(recordExtensionToJSObject(filter), includeNulls));
  }

  /**
   *
   * @param filter
   * @param hideSelectAll
   * @param selection Optionally, set the selected option after changing Show Select All option. Note that as of now,
   * it should only be used on single-select filters
   * @param persist Optionally, persist the selection as the new defaults for the filter. If set to true, it will also
   * result in applying all the filters.
   */
  updateHideSelectAll(
    filter: LegacyReportFilterControl,
    hideSelectAll: boolean,
    selection?: ReportTypes.FilterValueSelection,
    persist?: boolean
  ) {
    this.store.dispatch(
      new FilterPanelActions.SetHideSelectAll(recordExtensionToJSObject(filter), hideSelectAll, selection, persist)
    );
  }

  showOnlyRelevantValues(filterToken: string, showRelevantValues: boolean) {
    this.store.dispatch(new FilterPanelActions.ShowRelevantValues(filterToken, showRelevantValues));
  }
}
